/**
 * Knowledge Graph Page — Entity Relationship Visualization
 *
 * Three sections:
 *   - Connections: Fund -> Investments table showing connected investees + LPs
 *   - Risk Propagation: select entity -> show affected chain
 *   - Concentration Analysis: sector/country/instrument percentages
 *
 * Data from:
 *   GET /api/v1/impact-core/kg/graph       — full entity graph
 *   GET /api/v1/impact-core/kg/concentration — sector/country/instrument breakdown
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Network,
  AlertTriangle,
  PieChart,
  ChevronDown,
  ArrowRight,
  Loader2,
  Search,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';

// ─── Types ──────────────────────────────────────────────────────

interface KGNode {
  id: number;
  name: string;
  type: 'fund' | 'investee' | 'lp' | 'investment';
  sector?: string;
  country?: string;
  status?: string;
}

interface KGEdge {
  source_id: number;
  target_id: number;
  relation: string;
  amount?: number;
  currency?: string;
  instrument?: string;
}

interface KGGraph {
  nodes: KGNode[];
  edges: KGEdge[];
}

interface ConcentrationItem {
  label: string;
  count: number;
  amount: number;
  percentage: number;
}

interface ConcentrationData {
  by_sector: ConcentrationItem[];
  by_country: ConcentrationItem[];
  by_instrument: ConcentrationItem[];
}

type TabKey = 'connections' | 'risk' | 'concentration';

// ─── Main Component ─────────────────────────────────────────────

export function KnowledgeGraphPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('connections');
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [search, setSearch] = useState('');

  const graphQuery = useQuery<KGGraph>({
    queryKey: ['ic', 'kg', 'graph'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/kg/graph');
      return res.data?.data ?? res.data ?? { nodes: [], edges: [] };
    },
    staleTime: 60_000,
  });

  const concentrationQuery = useQuery<ConcentrationData>({
    queryKey: ['ic', 'kg', 'concentration'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/kg/concentration');
      return res.data?.data ?? res.data ?? { by_sector: [], by_country: [], by_instrument: [] };
    },
    enabled: activeTab === 'concentration',
    staleTime: 60_000,
  });

  const rawGraph = graphQuery.data ?? { nodes: [], edges: [] };
  const graph = {
    nodes: Array.isArray(rawGraph.nodes) ? rawGraph.nodes : [],
    edges: Array.isArray(rawGraph.edges) ? rawGraph.edges : [],
  };

  // Build lookup maps
  const nodeMap = useMemo(() => {
    const map = new Map<number, KGNode>();
    for (const node of graph.nodes) {
      if (node?.id != null) map.set(node.id, node);
    }
    return map;
  }, [graph.nodes]);

  // Fund -> connected entities table
  const fundConnections = useMemo(() => {
    const funds = graph.nodes.filter((n) => n.type === 'fund');
    return funds.map((fund) => {
      const outEdges = graph.edges.filter((e) => e.source_id === fund.id);
      const inEdges = graph.edges.filter((e) => e.target_id === fund.id);
      const investees = outEdges
        .map((e) => ({ ...e, node: nodeMap.get(e.target_id) }))
        .filter((e) => e.node?.type === 'investee' || e.node?.type === 'investment');
      const lps = inEdges
        .map((e) => ({ ...e, node: nodeMap.get(e.source_id) }))
        .filter((e) => e.node?.type === 'lp');
      return { fund, investees, lps };
    });
  }, [graph, nodeMap]);

  // Risk propagation chain
  const riskChain = useMemo(() => {
    if (!selectedEntityId) return [];
    const entityId = Number(selectedEntityId);
    const visited = new Set<number>();
    const chain: Array<{ node: KGNode; depth: number; relation: string }> = [];

    function traverse(nodeId: number, depth: number) {
      if (visited.has(nodeId) || depth > 5) return;
      visited.add(nodeId);

      const outgoing = graph.edges.filter((e) => e.source_id === nodeId);
      const incoming = graph.edges.filter((e) => e.target_id === nodeId);

      for (const edge of [...outgoing, ...incoming]) {
        const targetId = edge.source_id === nodeId ? edge.target_id : edge.source_id;
        const targetNode = nodeMap.get(targetId);
        if (targetNode && !visited.has(targetId)) {
          chain.push({ node: targetNode, depth, relation: edge.relation });
          traverse(targetId, depth + 1);
        }
      }
    }

    traverse(entityId, 1);
    return chain;
  }, [selectedEntityId, graph, nodeMap]);

  const filteredConnections = useMemo(() => {
    if (!search) return fundConnections;
    const q = search.toLowerCase();
    return fundConnections.filter(
      (fc) =>
        fc.fund.name.toLowerCase().includes(q) ||
        fc.investees.some((inv) => inv.node?.name.toLowerCase().includes(q)) ||
        fc.lps.some((lp) => lp.node?.name.toLowerCase().includes(q))
    );
  }, [fundConnections, search]);

  const selectedEntity = selectedEntityId ? nodeMap.get(Number(selectedEntityId)) : undefined;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">Knowledge Graph</h1>
        <p className="text-xs text-ic-text-tertiary">
          Entity relationships, risk propagation, and concentration analysis
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          <TabBtn
            label="Connections"
            icon={<Network className="h-3.5 w-3.5" />}
            active={activeTab === 'connections'}
            onClick={() => setActiveTab('connections')}
          />
          <TabBtn
            label="Risk Propagation"
            icon={<AlertTriangle className="h-3.5 w-3.5" />}
            active={activeTab === 'risk'}
            onClick={() => setActiveTab('risk')}
          />
          <TabBtn
            label="Concentration"
            icon={<PieChart className="h-3.5 w-3.5" />}
            active={activeTab === 'concentration'}
            onClick={() => setActiveTab('concentration')}
          />
        </div>
      </div>

      {/* Connections Tab */}
      {activeTab === 'connections' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary" />
            <input
              type="text"
              placeholder="Search funds, investees, LPs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-ic-border bg-ic-input py-2 pl-9 pr-3 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </div>

          {graphQuery.isLoading ? (
            <LoadingState />
          ) : graphQuery.isError ? (
            <ErrorMsg msg="Failed to load knowledge graph data." />
          ) : filteredConnections.length === 0 ? (
            <EmptyMsg msg="No fund connections found." />
          ) : (
            <div className="space-y-4">
              {filteredConnections.map((fc) => (
                <div key={fc.fund.id} className="rounded-lg border border-ic-border bg-ic-card p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Network className="h-4 w-4 text-ic-accent" />
                    <h3 className="text-xs font-semibold text-ic-text-primary">{fc.fund.name}</h3>
                    <StatusBadge label="Fund" variant="info" size="sm" />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Investees */}
                    <div>
                      <h4 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
                        Investees ({fc.investees.length})
                      </h4>
                      {fc.investees.length === 0 ? (
                        <p className="text-[10px] text-ic-text-tertiary">No investees connected</p>
                      ) : (
                        <div className="space-y-1.5">
                          {fc.investees.map((inv) => (
                            <div
                              key={`${inv.source_id}-${inv.target_id}`}
                              className="flex items-center justify-between rounded bg-ic-surface px-3 py-2"
                            >
                              <div className="flex items-center gap-2">
                                <ArrowRight className="h-3 w-3 text-ic-text-tertiary" />
                                <span className="text-xs text-ic-text-primary">{inv.node?.name ?? 'Unknown'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {inv.instrument && (
                                  <StatusBadge label={inv.instrument} variant="default" size="sm" />
                                )}
                                {inv.amount != null && (
                                  <span className="font-mono text-[10px] text-ic-text-secondary">
                                    {inv.currency ?? '$'}{inv.amount.toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* LPs */}
                    <div>
                      <h4 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
                        LPs ({fc.lps.length})
                      </h4>
                      {fc.lps.length === 0 ? (
                        <p className="text-[10px] text-ic-text-tertiary">No LPs connected</p>
                      ) : (
                        <div className="space-y-1.5">
                          {fc.lps.map((lp) => (
                            <div
                              key={`${lp.source_id}-${lp.target_id}`}
                              className="flex items-center gap-2 rounded bg-ic-surface px-3 py-2"
                            >
                              <ArrowRight className="h-3 w-3 text-emerald-400" />
                              <span className="text-xs text-ic-text-primary">{lp.node?.name ?? 'Unknown'}</span>
                              {lp.amount != null && (
                                <span className="ml-auto font-mono text-[10px] text-ic-text-secondary">
                                  {lp.currency ?? '$'}{lp.amount.toLocaleString()}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Risk Propagation Tab */}
      {activeTab === 'risk' && (
        <div className="space-y-4">
          {/* Entity Selector */}
          <div className="relative max-w-sm">
            <select
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              className="w-full appearance-none rounded-md border border-ic-border bg-ic-input px-3 py-2 pr-8 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
            >
              <option value="">Select an entity...</option>
              {graph.nodes.map((n) => (
                <option key={n.id} value={n.id}>
                  [{n.type}] {n.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
          </div>

          {graphQuery.isLoading ? (
            <LoadingState />
          ) : !selectedEntityId ? (
            <EmptyMsg msg="Select an entity to view its risk propagation chain." />
          ) : (
            <div className="space-y-4">
              {/* Selected Entity */}
              {selectedEntity && (
                <div className="rounded-lg border border-ic-accent/30 bg-ic-accent/5 p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-ic-accent" />
                    <span className="text-xs font-semibold text-ic-text-primary">{selectedEntity.name}</span>
                    <StatusBadge label={selectedEntity.type} variant="info" size="sm" />
                    {selectedEntity.sector && (
                      <StatusBadge label={selectedEntity.sector} variant="default" size="sm" />
                    )}
                  </div>
                  <p className="mt-1 text-[10px] text-ic-text-tertiary">
                    Risk event on this entity affects {riskChain.length} connected entit{riskChain.length === 1 ? 'y' : 'ies'}
                  </p>
                </div>
              )}

              {/* Chain */}
              {riskChain.length === 0 ? (
                <EmptyMsg msg="No connected entities found for risk propagation." />
              ) : (
                <div className="rounded-lg border border-ic-border bg-ic-card overflow-x-auto">
                  <table className="w-full text-xs ic-table">
                    <thead>
                      <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                        <th className="px-4 pb-2 pt-3 font-medium w-16">Depth</th>
                        <th className="px-4 pb-2 pt-3 font-medium">Entity</th>
                        <th className="px-4 pb-2 pt-3 font-medium">Type</th>
                        <th className="px-4 pb-2 pt-3 font-medium">Relation</th>
                        <th className="px-4 pb-2 pt-3 font-medium">Sector</th>
                        <th className="px-4 pb-2 pt-3 font-medium">Country</th>
                      </tr>
                    </thead>
                    <tbody>
                      {riskChain.map((item, idx) => (
                        <tr key={`${item.node.id}-${idx}`} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                          <td className="py-2.5 px-4">
                            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                              item.depth === 1 ? 'bg-red-500/15 text-red-400' :
                              item.depth === 2 ? 'bg-amber-500/15 text-amber-400' :
                              'bg-slate-500/15 text-slate-400'
                            }`}>
                              {item.depth}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{item.node.name}</td>
                          <td className="py-2.5 px-4">
                            <StatusBadge
                              label={item.node.type}
                              variant={item.node.type === 'fund' ? 'info' : item.node.type === 'lp' ? 'success' : 'default'}
                              size="sm"
                            />
                          </td>
                          <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{item.relation}</td>
                          <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{item.node.sector ?? '--'}</td>
                          <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{item.node.country ?? '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Concentration Tab */}
      {activeTab === 'concentration' && (
        <div className="space-y-6">
          {concentrationQuery.isLoading ? (
            <LoadingState />
          ) : concentrationQuery.isError ? (
            <ErrorMsg msg="Failed to load concentration data." />
          ) : (
            <>
              <ConcentrationSection
                title="By Sector"
                items={concentrationQuery.data?.by_sector ?? []}
                color="bg-ic-accent"
              />
              <ConcentrationSection
                title="By Country"
                items={concentrationQuery.data?.by_country ?? []}
                color="bg-emerald-500"
              />
              <ConcentrationSection
                title="By Instrument"
                items={concentrationQuery.data?.by_instrument ?? []}
                color="bg-amber-500"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Concentration Section ──────────────────────────────────────

function ConcentrationSection({
  title,
  items,
  color,
}: {
  title: string;
  items: ConcentrationItem[];
  color: string;
}) {
  if (items.length === 0) {
    return (
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">{title}</h3>
        <EmptyMsg msg={`No ${title.toLowerCase()} data available.`} />
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">{title}</h3>
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-ic-text-primary">{item.label}</span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] text-ic-text-secondary">
                    {item.count ?? 0} investment{(item.count ?? 0) !== 1 ? 's' : ''}
                  </span>
                  <span className="font-mono text-xs font-semibold text-ic-text-primary">
                    {(item.percentage != null && isFinite(item.percentage)) ? item.percentage.toFixed(1) : '0.0'}%
                  </span>
                </div>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-700/50">
                <div
                  className={`h-full rounded-full transition-[width] duration-500 ${color}`}
                  style={{ width: `${Math.min(100, item.percentage ?? 0)}%`, opacity: 0.7 }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function TabBtn({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
        active
          ? 'border-ic-accent text-ic-accent'
          : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function LoadingState() {
  return (
    <div className="flex h-48 items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
    </div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-red-400">{msg}</p>
    </div>
  );
}

function EmptyMsg({ msg }: { msg: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-ic-text-tertiary">{msg}</p>
    </div>
  );
}
